import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useTier } from '../../hooks/useTier';
import { useScope } from '../../hooks/useScope';
import {
  getCreditReportQuota,
  getParsedCreditReports,
  formatBureauName,
  getBureauTheme,
  formatTimestamp,
  previewCreditCardImport,
  importCreditCardsFromReports
} from '../../services/creditReportService';
import ReportUploader from './ReportUploader';
import ReportViewer from './ReportViewer';
import './ParseCreditReport.css';

const BUREAUS = ['experian', 'transunion', 'equifax'];

const ParseCreditReport = () => {
  const t = useTranslation();
  const { tierId, loading: tierLoading } = useTier();
  const { isClientView, isManaged, effectiveUserId, mentorId } = useScope();
  
  const [quota, setQuota] = useState(null);
  const [reports, setReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [activeBureau, setActiveBureau] = useState('experian');
  const [parseSuccess, setParseSuccess] = useState(null);
  
  // Import to inventory state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);

  // Determine view mode and scope parameters
  // isMentorMasquerade: mentor viewing/managing client's data (isManaged && !isClientView)
  // isInClientView: client viewing mentor's shared data (isManaged && isClientView)
  // Personal view: neither of the above (user viewing their own data)
  const isMentorMasquerade = isManaged && !isClientView;
  const isInClientView = isManaged && isClientView;
  
  // Parameters for API calls based on view mode
  const clientUserId = isMentorMasquerade ? effectiveUserId : null;
  const viewMentorId = isInClientView ? mentorId : null;

  // Load quota and existing reports
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load quota and reports in parallel with proper scope parameters
      const [quotaResult, reportsResult] = await Promise.all([
        getCreditReportQuota(clientUserId),
        getParsedCreditReports({ clientUserId, viewMentorId })
      ]);

      setQuota(quotaResult);

      // Organize reports by bureau
      const reportsByBureau = {};
      if (reportsResult.reports) {
        reportsResult.reports.forEach(report => {
          reportsByBureau[report.bureau] = report;
        });
      }
      setReports(reportsByBureau);
    } catch (err) {
      console.error('Error loading credit report data:', err);
      // Extract user-friendly error message
      const errorMessage = err.message || err.details || 'Failed to load credit report data';
      // Handle common error codes
      if (errorMessage.includes('INTERNAL') || errorMessage === 'INTERNAL') {
        setError('Service temporarily unavailable. Please try again in a moment.');
      } else if (errorMessage.includes('index') || errorMessage.includes('precondition')) {
        setError('System is updating. Please try again in a few minutes.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [clientUserId, viewMentorId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle successful parse
  const handleParseSuccess = (result) => {
    setParseSuccess(result);
    loadData(); // Reload data to show new report
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setParseSuccess(null);
    }, 5000);
  };

  // Handle viewing a report
  const handleViewReport = (report) => {
    setSelectedReport(report);
  };

  // Handle closing the viewer
  const handleCloseViewer = () => {
    setSelectedReport(null);
    loadData(); // Refresh in case of edits
  };

  // Check if there are any parsed reports
  const hasReports = Object.keys(reports).length > 0;

  // Handle import to inventory preview
  const handleImportPreview = async () => {
    setImportLoading(true);
    setImportError(null);
    
    try {
      const preview = await previewCreditCardImport(clientUserId);
      setImportPreview(preview);
      setShowImportModal(true);
    } catch (err) {
      console.error('Error previewing import:', err);
      setImportError(err.message || 'Failed to preview credit cards for import');
    } finally {
      setImportLoading(false);
    }
  };

  // Handle confirm import
  const handleConfirmImport = async () => {
    if (!importPreview?.cardsToImport?.length) return;
    
    setImportLoading(true);
    setImportError(null);
    
    try {
      const result = await importCreditCardsFromReports(
        importPreview.cardsToImport,
        clientUserId
      );
      
      setImportSuccess(result.message || `Successfully imported ${result.importedCount} cards`);
      setShowImportModal(false);
      setImportPreview(null);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setImportSuccess(null);
      }, 5000);
    } catch (err) {
      console.error('Error importing cards:', err);
      setImportError(err.message || 'Failed to import credit cards');
    } finally {
      setImportLoading(false);
    }
  };

  // Handle close import modal
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportPreview(null);
    setImportError(null);
  };

  // Check if user can parse
  // In client view mode (client viewing mentor's reports), parsing is disabled - only viewing
  // Only mentor masquerade or personal view can parse
  const canParse = quota?.canParse && !loading && !isInClientView;

  // Check tier access
  // Clients in client view mode can see reports created by their mentor
  const hasAccess = tierId === 'pro' || tierId === 'elite' || isInClientView || isMentorMasquerade;

  if (tierLoading || loading) {
    return (
      <div className="parse-credit-report">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('creditReportParser.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="parse-credit-report">
        <div className="access-denied-container">
          <div className="access-denied-card">
            <div className="access-icon">📊</div>
            <h2>{t('creditReportParser.accessDeniedTitle', 'Premium Feature')}</h2>
            <p>{t('creditReportParser.accessDeniedMessage', 'Credit report parsing is available for Pro and Elite members.')}</p>
            <div className="current-tier-badge">
              <span className="tier-label">{t('creditReportParser.currentTier', 'Current Tier')}:</span>
              <span className="tier-name">{tierId || 'Free'}</span>
            </div>
            <a href="/manage-subscription" className="upgrade-button">
              {t('creditReportParser.upgradeButton', 'Upgrade Now')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (selectedReport) {
    return (
      <ReportViewer 
        reportId={selectedReport.id} 
        onClose={handleCloseViewer}
        clientUserId={clientUserId}
      />
    );
  }

  return (
    <div className="parse-credit-report">
      <header className="parser-header">
        <div className="parse-credit-report-header-content">
          <h1>
            <span className="header-icon">📊</span>
            {t('creditReportParser.title', 'Credit Report Analyzer')}
          </h1>
          <p className="header-subtitle">
            {t('creditReportParser.subtitle', 'Upload your annual credit reports for AI-powered analysis')}
          </p>
        </div>
        
        <div className="header-actions">
          {/* Hide import button in client view mode - only mentor can import */}
          {hasReports && !isInClientView && (
            <button 
              className="import-to-inventory-btn"
              onClick={handleImportPreview}
              disabled={importLoading}
            >
              {importLoading ? (
                <>
                  <span className="btn-spinner"></span>
                  {t('creditReportParser.loading', 'Loading...')}
                </>
              ) : (
                <>
                  <span className="btn-icon">💳</span>
                  {t('creditReportParser.importToInventory', 'Import Cards to Inventory')}
                </>
              )}
            </button>
          )}
          
          {/* Hide quota badge in client view mode - client can't parse */}
          {quota && !isInClientView && (
            <div className="quota-badge">
              <div className="quota-info">
                <span className="quota-label">{t('creditReportParser.parsesRemaining', 'Parses Remaining')}</span>
                <span className="quota-value">{quota.remainingParses}</span>
              </div>
              <div className="tier-badge">
                <span>{quota.tier?.toUpperCase()}</span>
              </div>
            </div>
          )}
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {parseSuccess && (
        <div className="success-banner">
          <span className="success-icon">✓</span>
          {`${t('creditReportParser.parseSuccess', 'Successfully parsed')} ${formatBureauName(parseSuccess.bureau)} ${t('creditReportParser.report', 'report')}!`}
          <button className="close-banner" onClick={() => setParseSuccess(null)}>×</button>
        </div>
      )}

      {importSuccess && (
        <div className="success-banner">
          <span className="success-icon">✓</span>
          {importSuccess}
          <button className="close-banner" onClick={() => setImportSuccess(null)}>×</button>
        </div>
      )}

      {importError && !showImportModal && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {importError}
          <button className="close-banner" onClick={() => setImportError(null)}>×</button>
        </div>
      )}

      <div className="bureau-tabs">
        {BUREAUS.map(bureau => {
          const theme = getBureauTheme(bureau);
          const hasReport = !!reports[bureau];
          
          return (
            <button
              key={bureau}
              className={`bureau-tab ${activeBureau === bureau ? 'active' : ''} ${hasReport ? 'has-report' : ''}`}
              onClick={() => setActiveBureau(bureau)}
              style={{
                '--bureau-primary': theme.primary,
                '--bureau-secondary': theme.secondary,
                '--bureau-accent': theme.accent
              }}
            >
              <span className="bureau-name">{formatBureauName(bureau)}</span>
              {hasReport && <span className="report-indicator">✓</span>}
            </button>
          );
        })}
      </div>

      <div className="bureau-content">
        {BUREAUS.map(bureau => (
          <div 
            key={bureau}
            className={`bureau-panel ${activeBureau === bureau ? 'active' : ''}`}
          >
            <BureauPanel
              bureau={bureau}
              report={reports[bureau]}
              canParse={canParse}
              quota={quota}
              onParseSuccess={handleParseSuccess}
              onViewReport={handleViewReport}
              clientUserId={clientUserId}
              isInClientView={isInClientView}
              t={t}
            />
          </div>
        ))}
      </div>

      <div className="privacy-notice">
        <span className="privacy-icon">🔒</span>
        <p>
          {t('creditReportParser.privacyNotice', 'Your files are processed securely and never stored. Only the parsed data is saved to your account.')}
        </p>
      </div>

      {/* Import to Inventory Modal */}
      {showImportModal && (
        <ImportConfirmationModal
          preview={importPreview}
          loading={importLoading}
          error={importError}
          onConfirm={handleConfirmImport}
          onClose={handleCloseImportModal}
          t={t}
        />
      )}
    </div>
  );
};

// Import Confirmation Modal Component
const ImportConfirmationModal = ({ preview, loading, error, onConfirm, onClose, t }) => {
  const cardsToImport = preview?.cardsToImport || [];
  const hasCards = cardsToImport.length > 0;

  const formatCurrency = (amount) => {
    if (amount == null) return '--';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="import-modal" onClick={(e) => e.stopPropagation()}>
        <div className="import-modal-header">
          <h2>
            <span className="modal-icon-parse-credit-report">💳</span>
            {t('creditReportParser.importModalTitle', 'Import Credit Cards to Inventory')}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="import-modal-body">
          {error && (
            <div className="import-modal-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {!hasCards && !error && (
            <div className="import-modal-empty">
              <span className="empty-icon">✓</span>
              <h3>{t('creditReportParser.noNewCards', 'No New Cards Found')}</h3>
              <p>{t('creditReportParser.noNewCardsMessage', 'All credit cards from your reports are already in your inventory.')}</p>
            </div>
          )}

          {hasCards && (
            <>
              <div className="import-summary">
                <div className="summary-stat">
                  <span className="stat-value">{preview.totalCardsInReports || 0}</span>
                  <span className="stat-label">{t('creditReportParser.cardsInReports', 'Cards in Reports')}</span>
                </div>
                <div className="summary-stat">
                  <span className="stat-value">{preview.existingInInventory || 0}</span>
                  <span className="stat-label">{t('creditReportParser.alreadyInInventory', 'Already in Inventory')}</span>
                </div>
                <div className="summary-stat highlight">
                  <span className="stat-value">{cardsToImport.length}</span>
                  <span className="stat-label">{t('creditReportParser.cardsToAdd', 'Cards to Add')}</span>
                </div>
              </div>

              <div className="import-cards-list">
                <h3>{t('creditReportParser.cardsToBeImported', 'Cards to be imported')}:</h3>
                <div className="cards-preview-grid">
                  {cardsToImport.map((card, index) => (
                    <div key={index} className="card-preview-item">
                      <div className="card-preview-header">
                        <span className="card-icon">💳</span>
                        <span className="card-name">{card.creditorName || t('creditReportParser.unknownCard', 'Unknown Card')}</span>
                        {card.last4Digits && (
                          <span className="card-last4">••••{card.last4Digits}</span>
                        )}
                      </div>
                      <div className="card-preview-details">
                        <div className="detail">
                          <span className="detail-label">{t('creditReportParser.creditLimit', 'Credit Limit')}:</span>
                          <span className="detail-value">{formatCurrency(card.creditLimit)}</span>
                        </div>
                        {card.currentBalance != null && (
                          <div className="detail">
                            <span className="detail-label">{t('creditReportParser.balance', 'Balance')}:</span>
                            <span className="detail-value">{formatCurrency(card.currentBalance)}</span>
                          </div>
                        )}
                        {card.dateOpened && (
                          <div className="detail">
                            <span className="detail-label">{t('creditReportParser.opened', 'Opened')}:</span>
                            <span className="detail-value">{card.dateOpened}</span>
                          </div>
                        )}
                        {card.bureaus && card.bureaus.length > 0 && (
                          <div className="detail bureaus">
                            <span className="detail-label">{t('creditReportParser.reportedBy', 'Reported by')}:</span>
                            <span className="detail-value bureau-tags">
                              {card.bureaus.map((b, i) => (
                                <span key={i} className={`bureau-tag ${b}`}>{b}</span>
                              ))}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="import-modal-footer">
          <button 
            className="btn-cancel" 
            onClick={onClose}
            disabled={loading}
          >
            {t('creditReportParser.cancel', 'Cancel')}
          </button>
          {hasCards && (
            <button 
              className="btn-confirm"
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  {t('creditReportParser.importing', 'Importing...')}
                </>
              ) : (
                <>
                  <span className="btn-icon">✓</span>
                  {t('creditReportParser.confirmImport', 'Import')} {cardsToImport.length} {t('creditReportParser.cards', 'Cards')}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Bureau Panel Component
const BureauPanel = ({ bureau, report, canParse, quota, onParseSuccess, onViewReport, clientUserId, isInClientView, t }) => {
  const theme = getBureauTheme(bureau);
  return (
    <div className="bureau-panel-content" style={{ '--bureau-gradient': theme.gradient }}>
      {report ? (
        <div className="existing-report">
          <div className="report-card" onClick={() => onViewReport(report)}>
            <div className="report-header" style={{ background: theme.gradient }}>
              <span className="bureau-logo">{formatBureauName(bureau)}</span>
              <span className="report-date">
                {formatTimestamp(report.parsedAt)}
              </span>
            </div>
            <div className="report-body">
              <div className="report-stats">
                <div className="stat">
                  <span className="stat-value">{report.summary?.totalAccounts || 0}</span>
                  <span className="stat-label">{t('creditReportParser.accounts', 'Accounts')}</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{report.summary?.hardInquiries || 0}</span>
                  <span className="stat-label">{t('creditReportParser.inquiries', 'Inquiries')}</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{report.summary?.collections || 0}</span>
                  <span className="stat-label">{t('creditReportParser.collections', 'Collections')}</span>
                </div>
              </div>
              <button className="view-report-btn">
                {t('creditReportParser.viewReport', 'View Full Report')} →
              </button>
            </div>
          </div>
          
          {canParse && (
            <div className="reparse-option">
              <p className="reparse-note">
                {t('creditReportParser.reparseNote', 'Want to update? Upload a new report to replace the existing one.')}
              </p>
              <ReportUploader
                bureau={bureau}
                onSuccess={onParseSuccess}
                clientUserId={clientUserId}
                compact
              />
            </div>
          )}
        </div>
      ) : (
        <div className="no-report">
          {canParse ? (
            <ReportUploader
              bureau={bureau}
              onSuccess={onParseSuccess}
              clientUserId={clientUserId}
            />
          ) : (
            <div className="rate-limit-message">
              <span className="limit-icon">{isInClientView ? '👀' : '⏳'}</span>
              {isInClientView ? (
                <>
                  <h3>{t('creditReportParser.viewOnlyTitle', 'View Only Mode')}</h3>
                  <p>{t('creditReportParser.viewOnlyMessage', 'Your mentor can upload credit reports for you. Contact them to request a report analysis.')}</p>
                </>
              ) : (
                <>
                  <h3>{t('creditReportParser.rateLimitTitle', 'Parse Limit Reached')}</h3>
                  <p>{quota?.reason || t('creditReportParser.rateLimitMessage', 'You have used all your credit report parses for this year.')}</p>
                  {quota?.lastParseDate && (
                    <p className="next-available">
                      {t('creditReportParser.lastParsed', 'Last parsed')}: {formatTimestamp(quota.lastParseDate)}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ParseCreditReport;

